// src/services/analytics.js
//
// Analytics for Profound Philosophy, on the fleet's consent-aware GA4 rail
// (@ddd/analytics — see docs/FLEET_SEO_ANALYTICS_STANDARD.md, layer 3).
//
// The single source of truth for consent is the user's own `analytics`
// preference on the Data & Privacy page, stored device-locally alongside the
// rest of their preferences. Google measures nothing until they say yes:
// @ddd/analytics injects the gtag loader only after consent, so before that
// the app makes no request to Google Analytics. (Cloudflare Web Analytics,
// injected at the edge, counts visits in totals for everyone.)
//
// The measurement ID is a literal on purpose. It is public by nature, and an
// env var would silently disable analytics in any worktree without a `.env`
// — which is exactly how this app went dark in the first place.
import { initAnalytics, setAnalyticsConsent, getStoredConsent } from '@ddd/analytics';

export const MEASUREMENT_ID = 'G-VEP2FEQQL4';

// Owned here rather than in AppContext so the boot path can read the consent
// decision before React mounts, without importing the provider.
export const PREFERENCES_KEY = 'profoundPhilosophy_preferences';

/** The user's stored analytics decision. Defaults to false — opt-in, never opt-out. */
export function hasAnalyticsPreference() {
  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    return stored ? JSON.parse(stored).analytics === true : false;
  } catch {
    return false;
  }
}

/**
 * Call once at startup, before React mounts. Loads GA4 only if the user has
 * already opted in; otherwise it arms the helper and waits for the toggle.
 */
export function startAnalytics() {
  initAnalytics({ measurementId: MEASUREMENT_ID, appId: 'profound-philosophy' });
  // Re-assert the app preference on every boot so it stays authoritative over
  // the helper's own stored copy — one switch, one answer. But only when a
  // real decision exists: writing 'denied' for a visitor who has never been
  // asked would erase the never-asked state the first-visit notice keys on.
  if (getStoredConsent() !== null || hasAnalyticsPreference()) {
    setAnalyticsConsent(hasAnalyticsPreference());
  }
}

export { setAnalyticsConsent };
